import { FC, useMemo, useState, useEffect } from 'react';
import SimpleBar from 'simplebar-react';
import { useSelector } from 'react-redux';
import { Layout, Menu } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { checkIsSuperUser } from 'config/permissions';
import useStickyState from 'utils/useStickyState';
import { getRouteConfig, MenuItemBaseLinkType } from './config';
import MenuItem from './MenuItem';
import styles from './index.module.less';

const { Sider } = Layout;

const version = process.env.REACT_APP_GIT_HASH ? process.env.REACT_APP_GIT_HASH.substring(0, 6) : null;

interface SidebarProps {
  activeRoute?: MenuItemBaseLinkType;
}

const Sidebar: FC<SidebarProps> = ({ activeRoute }) => {
  const user = useSelector((store: any) => store.auth.user);
  const [collapsed, setCollapsed] = useStickyState(false, '@ls-admin/sidebar-collapsed');
  const [forceExpanded, setForceExpanded] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const breakpoints = useBreakpoint();
  const isSuperUser = useMemo(() => checkIsSuperUser(user?.email), [user?.email]);
  const config = useMemo(() => getRouteConfig(), []);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);
  const [menuHeight, setMenuHeight] = useState(0);

  useEffect(() => {
    if (!menuEl) return undefined;
    const ro = new ResizeObserver(() => setMenuHeight(menuEl.offsetHeight));
    ro.observe(menuEl);
    return () => ro.disconnect();
  }, [menuEl]);

  if (!breakpoints.sm) {
    return null;
  }
  const effectiveCollapsed = collapsed && !forceExpanded;

  const handleOpenChange = (keys: string[]) => {
    // Allow opening/closing based on effectiveCollapsed state
    if (!effectiveCollapsed) {
      setOpenKeys(keys);
    } else if (keys.length === 0) {
      setOpenKeys([]);
      setForceExpanded(false);
    }
  };

  return (
    <Sider
      collapsed={effectiveCollapsed}
      collapsedWidth={80}
      width={200}
      theme="light"
      trigger={null}
      className={styles.sidebar}
    >
      <div className={styles.menuWrapper}>
        <SimpleBar className={styles.simpleBar}>
          <div ref={setMenuEl}>
            <Menu
              mode="inline"
              theme="light"
              selectable={false}
              inlineCollapsed={effectiveCollapsed}
              defaultOpenKeys={openKeys}
              onOpenChange={handleOpenChange}
            >
              {config.map((item) => (
                <MenuItem
                  key={`${'path' in item ? item.path : item.key}`}
                  item={item}
                  user={user}
                  selectedItem={activeRoute && 'path' in activeRoute ? activeRoute : undefined}
                  classNameLabel={styles.itemLabel}
                  isSuperUser={isSuperUser}
                  collapsed={effectiveCollapsed}
                />
              ))}
            </Menu>
          </div>
        </SimpleBar>
      </div>
      <button
        type="button"
        className={styles.collapseHandle}
        onClick={() => {
          setCollapsed((c: boolean) => !c);
          setForceExpanded(false);
        }}
        title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <span
          className={styles.collapseHandleIcon}
          style={menuHeight > 0 ? { top: menuHeight / 2 - 12 } : undefined}
        >
          {effectiveCollapsed ? <RightOutlined /> : <LeftOutlined />}
        </span>
      </button>

      <div className={styles.footer}>
        <div className={styles.version}>{version ? `Ver: ${version}` : undefined}</div>
      </div>
    </Sider>
  );
};

export default Sidebar;
